import type { Plan } from "@circle-react/types";
import { useMemo } from "react";

import { useFetchProration } from "@/react/components/shared/UpgradePlan/hooks/useFetchProration";
import type { ProrationDisplayData } from "@/react/components/shared/UpgradePlan/types";

export interface CalculatedPricing {
  annualSavings?: string;
  currentPlanCredit?: string;
  discountCoupon?: string;
  dueToday?: string;
  isAnnualPlan?: boolean;
}

interface UsePlanPricingProps {
  alternativePlan?: Plan;
  isAnnualPlan: boolean;
  isPlanLoading: boolean;
  plan?: Plan;
  planCouponId?: number;
  shouldSkipProration?: boolean;
}

const extractProrationPricing = (
  prorationData: ProrationDisplayData,
): CalculatedPricing => ({
  currentPlanCredit:
    prorationData.display_prorated_charges_credit_amount_compact,
  discountCoupon: prorationData.display_total_discount_compact,
  dueToday: prorationData.display_total_due_today_compact,
});

const calculateDueTodayAmount = (plan: Plan, isAnnualPlan: boolean): string => {
  if (plan.amount && typeof plan.amount === "number") {
    return `$${(plan.amount / 100).toFixed(0)}`;
  }

  if (isAnnualPlan) {
    return plan.annual_base_amount_with_currency || "";
  }

  return plan.display_base_monthly_amount || "";
};

export const usePlanPricing = ({
  plan,
  alternativePlan,
  isAnnualPlan,
  planCouponId,
  isPlanLoading,
  shouldSkipProration = false,
}: UsePlanPricingProps) => {
  // Trials get a new-subscription preview from the same endpoint
  const shouldFetchProration = !shouldSkipProration && !!plan?.id;

  const { prorationData, isProrationLoading, errorFetchingProration } =
    useFetchProration({
      new_plan_id: plan?.id || 0,
      plan_coupon_id: planCouponId ?? plan?.plan_coupon?.id,
      only_ch: true,
      enabled: shouldFetchProration,
    });

  const pricing = useMemo((): CalculatedPricing => {
    if (!plan) return {};

    let calculatedPricing: CalculatedPricing = {};

    // Use proration data if available
    if (prorationData && !errorFetchingProration) {
      calculatedPricing = extractProrationPricing(prorationData);
    }

    // Always include annual savings (regardless of interval)
    // Use the annual plan's discount amount if we're on annual, otherwise use the alternative plan's discount
    if (!calculatedPricing.annualSavings) {
      const annualPlan = isAnnualPlan ? plan : alternativePlan;
      if (annualPlan?.annual_discount_amount_with_currency) {
        calculatedPricing.annualSavings = `-${annualPlan.annual_discount_amount_with_currency}`;
      }
    }

    // Calculate due today if not set by proration
    if (!calculatedPricing.dueToday) {
      calculatedPricing.dueToday = calculateDueTodayAmount(plan, isAnnualPlan);

      // Without a preview we can't price the offer, but still show that one applies
      if (plan.is_on_offer && plan.discount_percentage) {
        calculatedPricing.discountCoupon = plan.discount_percentage;
      }
    }

    // Add the isAnnualPlan flag to the pricing object
    calculatedPricing.isAnnualPlan = isAnnualPlan;

    return calculatedPricing;
  }, [
    plan,
    alternativePlan,
    isAnnualPlan,
    prorationData,
    errorFetchingProration,
  ]);

  // Only consider proration loading if we're actually fetching proration
  const isLoadingPricing = !!(
    isPlanLoading ||
    (shouldFetchProration && isProrationLoading)
  );

  return {
    pricing,
    isLoadingPricing,
  };
};
